import React, { useState, useEffect } from 'react';
import { Container, Card, Button, Badge, Form, Alert } from 'react-bootstrap';
import axios from 'axios';
import { API_URL } from '../config';
import { useTheme } from '../theme/ThemeContext';

function SelfServiceView() {
  const [drinks, setDrinks] = useState([]);
  const [order, setOrder] = useState({});
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [showSoldOut, setShowSoldOut] = useState(false);
  const { darkMode } = useTheme();
  const [customerName, setCustomerName] = useState('');

  useEffect(() => {
    fetchDrinks();
  }, []);

  const fetchDrinks = async () => {
    try {
      const response = await axios.get(`${API_URL}/drinks`);
      setDrinks(response.data);
    } catch (error) {
      setError('Fehler beim Laden der Getränke');
      console.error('Error fetching drinks:', error);
    }
  };

  const handleIncrement = (drinkId) => {
    const drink = drinks.find(d => d.id === drinkId);
    if (drink.sold_out) {
      setError('Dieses Getränk ist leider ausverkauft');
      return;
    }
    setOrder(prev => ({
      ...prev,
      [drinkId]: (prev[drinkId] || 0) + 1
    }));
    setError(null);
  };

  const handleDecrement = (drinkId) => {
    if (order[drinkId]) {
      setOrder(prev => ({
        ...prev,
        [drinkId]: Math.max(0, (prev[drinkId] || 0) - 1)
      }));
    }
    setError(null);
  };

  const handleSubmit = async () => {
    if (!customerName.trim()) {
      setError('Bitte geben Sie Ihren Namen ein');
      return;
    }

    const orderItems = Object.entries(order)
      .filter(([_, quantity]) => quantity > 0)
      .map(([drinkId, quantity]) => ({
        drink_id: parseInt(drinkId),
        quantity
      }));

    if (orderItems.length === 0) {
      setError('Bitte wählen Sie mindestens ein Getränk');
      return;
    }

    try {
      await axios.post(`${API_URL}/orders`, {
        items: orderItems,
        customer_name: customerName,
        status: 'pending'
      });
      setSuccess(true);
      setOrder({});
      setCustomerName('');
      setTimeout(() => setSuccess(false), 5000);
    } catch (error) {
      setError('Fehler beim Erstellen der Bestellung');
      console.error('Error creating order:', error);
    }
  };

  const filteredDrinks = showSoldOut 
    ? drinks 
    : drinks.filter(drink => !drink.sold_out);

  const totalAmount = Object.entries(order)
    .reduce((sum, [drinkId, quantity]) => {
      const drink = drinks.find(d => d.id === parseInt(drinkId));
      return sum + (drink ? drink.price * quantity : 0);
    }, 0);

  return (
    <Container className="mt-4 pb-4">
      <h2 className="mb-4">Selbstbedienung</h2>

      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible>
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" onClose={() => setSuccess(false)} dismissible>
          Ihre Bestellung wurde erfolgreich aufgenommen! Bitte warten Sie auf die Zubereitung.
        </Alert>
      )}

      <Form.Group className="mb-4">
        <Form.Label>Ihr Name</Form.Label>
        <Form.Control
          type="text"
          value={customerName}
          onChange={(e) => setCustomerName(e.target.value)}
          placeholder="Name für die Bestellung"
        />
      </Form.Group>

      <Form.Check
        type="switch"
        id="show-sold-out"
        label="Ausverkaufte Getränke anzeigen"
        checked={showSoldOut}
        onChange={(e) => setShowSoldOut(e.target.checked)}
        className="mb-4"
      />

      <div className="drink-grid">
        {filteredDrinks.map((drink) => (
          <Card 
            key={drink.id} 
            className={`mb-3 ${drink.sold_out ? 'bg-light' : ''}`}
            style={{
              backgroundColor: darkMode ? (drink.sold_out ? '#424242' : '#303030') : (drink.sold_out ? '#f8f9fa' : '#ffffff'),
              color: darkMode ? '#ffffff' : '#000000',
              borderColor: darkMode ? '#424242' : undefined
            }}
          >
            <Card.Body>
              <Card.Title className="d-flex justify-content-between align-items-center">
                {drink.name}
                {drink.sold_out && (
                  <Badge bg="danger">Ausverkauft</Badge>
                )}
              </Card.Title>
              <Card.Text>
                CHF {drink.price.toFixed(2)}
                <br />
                {drink.description}
              </Card.Text>
              <div className="d-flex justify-content-between align-items-center">
                <Button 
                  variant={darkMode ? "outline-light" : "outline-dark"}
                  onClick={() => handleDecrement(drink.id)}
                  disabled={!order[drink.id] || drink.sold_out}
                >
                  -
                </Button>
                <span>{order[drink.id] || 0}</span>
                <Button 
                  variant={darkMode ? "outline-light" : "outline-dark"}
                  onClick={() => handleIncrement(drink.id)}
                  disabled={drink.sold_out}
                >
                  +
                </Button>
              </div>
            </Card.Body>
          </Card>
        ))}
      </div>

      <Card className="mt-4">
        <Card.Body>
          <Card.Title>Ihre Bestellung</Card.Title>
          <Card.Text>
            Gesamtbetrag: CHF {totalAmount.toFixed(2)}
          </Card.Text>
          <Button 
            variant="primary" 
            onClick={handleSubmit}
            disabled={!customerName.trim() || Object.values(order).every(v => !v)}
          >
            Bestellen
          </Button>
        </Card.Body>
      </Card>
    </Container>
  );
}

export default SelfServiceView;
